import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { riotballTeams } from "../data/riotballTeams";

/* ARENAS OF THE LEAGUE — browse all ten home grounds: the painted stadium
   (crowd + fence) on the card, and a lightbox with the stadium plus the
   franchise's neon court floor full-size. */
const FLOOR_BY_FRANCHISE = {
  "red-force": "rust",
  "white-guards": "chrome",
  silverbacks: "steel",
  "verdigris-vanguard": "verdant",
  "aurora-syndicate": "amber",
  "voltaic-vultures": "volt",
  "obsidian-legion": "ember",
  "chrome-phantoms": "azure",
  "night-ravens": "neon",
  "iron-hearts": "crimson",
};

export function ArenaGallery() {
  const [open, setOpen] = useState(null);
  const team = open !== null ? riotballTeams[open] : null;

  useEffect(() => {
    if (open === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(null);
      if (e.key === "ArrowRight") setOpen((v) => (v + 1) % riotballTeams.length);
      if (e.key === "ArrowLeft") setOpen((v) => (v - 1 + riotballTeams.length) % riotballTeams.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="arena-gallery" data-testid="arena-gallery">
      <div className="arena-grid">
        {riotballTeams.map((t, i) => (
          <button
            key={t.id}
            type="button"
            className="arena-card"
            data-testid={`arena-card-${t.id}`}
            style={{ "--team-color": t.color }}
            onClick={() => setOpen(i)}
            aria-label={`Open ${t.name} home arena — ${t.home}`}
          >
            <img src={`/assets/arenas/arena-${t.id}.jpg`} alt={`${t.name} home arena — ${t.home}`} loading="lazy" />
            <span className="arena-card-meta">
              <strong>{t.name}</strong>
              <em>{t.home}</em>
            </span>
          </button>
        ))}
      </div>
      <AnimatePresence>
        {team ? (
          <motion.div
            className="arena-lightbox"
            data-testid="arena-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(null)}
          >
            <motion.div
              className="arena-lightbox-inner"
              style={{ "--team-color": team.color }}
              initial={{ scale: 0.94, y: 14 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 10 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="arena-lightbox-head" style={{ color: team.color }}>
                <strong>{team.name}</strong>
                <em>{team.home}</em>
              </div>
              <img
                className="arena-lightbox-stadium"
                src={`/assets/arenas/arena-${team.id}.jpg`}
                alt={`${team.name} stadium — crowd and fence`}
                data-testid="arena-lightbox-stadium"
              />
              <img
                className="arena-lightbox-floor"
                src={`/assets/arenas/floor-${FLOOR_BY_FRANCHISE[team.id]}.jpg`}
                alt={`${team.name} home court floor`}
                data-testid="arena-lightbox-floor"
              />
              <span className="arena-lightbox-sub">THE COURT — GAME NIGHT FLOOR</span>
              <button type="button" className="arena-lightbox-close" data-testid="arena-lightbox-close" onClick={() => setOpen(null)} aria-label="Close arena viewer">
                <X size={16} />
              </button>
              <button
                type="button"
                className="arena-lightbox-nav prev"
                data-testid="arena-lightbox-prev"
                onClick={() => setOpen((v) => (v - 1 + riotballTeams.length) % riotballTeams.length)}
                aria-label="Previous arena"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                className="arena-lightbox-nav next"
                data-testid="arena-lightbox-next"
                onClick={() => setOpen((v) => (v + 1) % riotballTeams.length)}
                aria-label="Next arena"
              >
                <ChevronRight size={20} />
              </button>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
